import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { api } from '../lib/api.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Los parametros de operacion de la plataforma.
 *
 * `MOD_AJUSTES` estaba en el enum de modulos desde la migracion 0001 y en la
 * matriz de permisos desde la 0003 —ADMIN escribe, GERENTE lee— y no abria
 * nada. Al mismo tiempo la torre de control traia doce numeros escritos
 * dentro de su vista, todos razonables y todos adivinados: se eligieron sin
 * haber operado un mes con ellos, y cambiar uno era escribir una migracion y
 * desplegar.
 *
 * Esta pantalla no sabe cuales son esos parametros ni que rango tienen: la
 * lista, las etiquetas, las unidades, los limites y los valores de fabrica
 * vienen de la base. Una pantalla con su propia copia de los limites ofrece
 * un valor que el servidor rechaza, o peor, al reves.
 */

interface Ajuste {
  clave: string;
  etiqueta: string;
  ayuda: string;
  unidad: string;
  grupo: string;
  minimo: string;
  maximo: string;
  por_omision: string;
  valor: string;
  ajustado: boolean;
  actualizado_en: string | null;
}

/** Numeros sin cola de ceros: 12.00 se escribe 12. */
const limpio = (n: string): string => String(Number(n));

export function Ajustes(): JSX.Element {
  const { puede } = useSesion();
  const escribe = puede('MOD_AJUSTES', 'escribir');

  const [filas, setFilas] = useState<Ajuste[] | null>(null);
  const [edicion, setEdicion] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);

  const adoptar = useCallback((datos: Ajuste[]) => {
    setFilas(datos);
    setEdicion(Object.fromEntries(datos.map((a) => [a.clave, limpio(a.valor)])));
  }, []);

  const cargar = useCallback(() => {
    api
      .obtener<Ajuste[]>('/v1/ajustes')
      .then(({ datos }) => {
        adoptar(datos);
        setError(null);
      })
      .catch(setError);
  }, [adoptar]);

  useEffect(() => cargar(), [cargar]);

  /* Lo que cambio respecto a lo que la base contesto. Se manda TODO junto y
     no renglon por renglon: hay pares que se validan uno contra otro —el
     urgente no puede pasar del aviso—, asi que bajar los dos exige que los
     dos lleguen en la misma sentencia. De a uno, el primero choca y no hay
     forma de llegar al estado que se quiere. */
  const cambios = (filas ?? [])
    .filter((a) => (edicion[a.clave] ?? '') !== limpio(a.valor))
    .map((a) => ({ clave: a.clave, valor: Number(edicion[a.clave] ?? '') }));

  const invalidos = cambios.filter((c) => !Number.isFinite(c.valor));

  async function guardar(): Promise<void> {
    setGuardando(true);
    try {
      const { datos } = await api.poner<Ajuste[]>('/v1/ajustes', { cambios });
      adoptar(datos);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  async function restablecer(clave: string): Promise<void> {
    setGuardando(true);
    try {
      const { datos } = await api.quitar<Ajuste[]>(`/v1/ajustes/${encodeURIComponent(clave)}`);
      adoptar(datos);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  const grupos = [...new Set((filas ?? []).map((a) => a.grupo))];

  return (
    <>
      <Pagehead />

      {error !== null && <AvisoError error={error} />}

      {filas === null ? (
        <Cargando />
      ) : (
        <>
          <div className="card">
            <p className="mini" style={{ margin: 0 }}>
              De aqui salen los umbrales con los que la torre de control decide que avisar y con
              cuanta urgencia. Un umbral mal puesto no da un error: hace que la torre avise de todo
              o de nada, y en los dos casos deja de leerse.
            </p>
          </div>

          {grupos.map((g) => (
            <div className="card" key={g} style={{ marginTop: '14px' }} data-prueba="grupo-ajuste">
              <h2 style={{ marginTop: 0 }}>{g}</h2>
              <div className="tabla-mapa">
                <table data-prueba="tabla-ajustes">
                  <thead>
                    <tr>
                      <th scope="col">Parametro</th>
                      <th scope="col" className="num">
                        Valor
                      </th>
                      <th scope="col">De fabrica</th>
                      {escribe && <th scope="col" />}
                    </tr>
                  </thead>
                  <tbody>
                    {filas
                      .filter((a) => a.grupo === g)
                      .map((a) => (
                        <tr key={a.clave} data-prueba="fila-ajuste">
                          <th scope="row">
                            {a.etiqueta}
                            <div className="mini">{a.ayuda}</div>
                          </th>
                          <td className="num">
                            {escribe ? (
                              <>
                                <input
                                  value={edicion[a.clave] ?? ''}
                                  onChange={(e) =>
                                    setEdicion({ ...edicion, [a.clave]: e.target.value })
                                  }
                                  inputMode="decimal"
                                  aria-label={a.etiqueta}
                                  aria-describedby={`${a.clave}-rango`}
                                  style={{ width: 80, textAlign: 'right' }}
                                  data-prueba={`ajuste-${a.clave}`}
                                />{' '}
                                {a.unidad}
                                <div className="mini" id={`${a.clave}-rango`}>
                                  de {limpio(a.minimo)} a {limpio(a.maximo)}
                                </div>
                              </>
                            ) : (
                              <span data-prueba={`valor-${a.clave}`}>
                                {limpio(a.valor)} {a.unidad}
                              </span>
                            )}
                          </td>
                          <td>
                            {/* "De fabrica" o "ajustado" son cosas distintas y
                                se parecen mucho: sin esta columna son doce
                                numeros y no se sabe cual es una decision de
                                alguien y cual es el valor que nadie ha mirado
                                nunca. */}
                            {a.ajustado ? (
                              <span className="tag t-ok" data-prueba={`ajustado-${a.clave}`}>
                                ajustado · de fabrica {limpio(a.por_omision)}
                              </span>
                            ) : (
                              <span className="tag t-mute">de fabrica</span>
                            )}
                          </td>
                          {escribe && (
                            <td>
                              {a.ajustado && (
                                <button
                                  type="button"
                                  className="btn ghost sm"
                                  onClick={() => void restablecer(a.clave)}
                                  disabled={guardando}
                                  data-prueba={`restablecer-${a.clave}`}
                                >
                                  Restablecer
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {escribe && (
            <div className="card toolbar" style={{ marginTop: '14px' }}>
              <span className="mini" data-prueba="ajustes-pendientes">
                {cambios.length === 0
                  ? 'Sin cambios pendientes'
                  : `${cambios.length} ${cambios.length === 1 ? 'cambio' : 'cambios'} sin guardar`}
              </span>
              <span className="sp" />
              <button
                type="button"
                className="btn ghost"
                onClick={() => adoptar(filas)}
                disabled={cambios.length === 0 || guardando}
                data-prueba="descartar-ajustes"
              >
                Descartar
              </button>
              <button
                type="button"
                className="btn pri"
                onClick={() => void guardar()}
                /* Todos juntos, y nada si hay un campo que no es un numero:
                   mandar `NaN` al servidor para que lo rechace es gastarle un
                   viaje a alguien que solo dejo el campo a medias. */
                disabled={cambios.length === 0 || invalidos.length > 0 || guardando}
                data-prueba="guardar-ajustes"
              >
                {guardando ? '...' : 'Guardar ajustes'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
